import { isDialoguePerformance, type DialoguePerformanceV1 } from "../../../shared/src/social/performance";
import { ARTISAN_LISTENING_FACE, resolveDelivery, type DeliveryPattern } from "./conversationPatterns";
import { dialogueGlyphs, glyphDelay, type DialogueSpan, type DialogueGlyph } from "../../../shared/src/social/dialogue";
import type { FaceFrame } from "./faceRig";

export type ConversationPhase = "activity" | "attending" | "listening" | "preparing" | "performing" | "releasing";
export interface ConversationPose {
  face: FaceFrame;
  pitch: number;
  /** 0 = authored work target, 1 = listener. No generated coordinates. */
  attention: number;
}
export interface ConversationActor {
  apply(pose: ConversationPose | null): void;
  mouth(character: string | null, style?: DialogueGlyph["style"]): void;
}
export interface PresentationOptions { reducedMotion: boolean; instantText: boolean }
interface CompiledSegment {
  id: string; text: string; deliveryId: string; pattern: DeliveryPattern; fallback: boolean;
  glyphs: DialogueGlyph[]; offset: number; chars: string[]; revealAt: number[]; start: number; end: number; nodAt: number;
}
const ATTEND = 0.35, RELEASE = 0.3, PREPARE_TIMEOUT = 12;
const SPEECH_TEMPO = 3;
const clamp = (v: number): number => Math.max(0, Math.min(1, v));
const smooth = (v: number): number => { const t = clamp(v); return t * t * (3 - 2 * t); };

/** One presentation clock, no timers. The caller owns scene/turn identities and cancellation.
 * Local, in-place prototype; this is not a multiplayer lease or a memory authority. */
export class ConversationDirector {
  private phase: ConversationPhase = "activity";
  private phaseTime = 0;
  private sceneId: string | null = null;
  private turnId: string | null = null;
  private accepted = false;
  private usedTurns = new Set<string>();
  private segments: CompiledSegment[] = [];
  private time = 0;
  private duration = 0;
  private fullText = "";
  private text = "";
  private resume: "listen" | "activity" = "listen";
  private lastChar = "";
  private error: string | null = null;
  private pose: ConversationPose = { face: {}, pitch: 0, attention: 0 };
  private releaseFrom: ConversationPose = this.pose;
  private options: PresentationOptions = { reducedMotion: false, instantText: false };

  private spans: DialogueSpan[] = [];
  private pageEnds: number[] = [];
  private pageIndex = 0;
  private awaitingPage = false;
  private readThrough = 0;

  constructor(private readonly actor: ConversationActor,
    private readonly paginate?: (text: string, spans: readonly DialogueSpan[]) => number[]) {}

  /** A resize may reflow unread text, but never rewinds the speaking clock. */
  setPageEnds(ends: number[]): void {
    this.pageEnds = [...new Set(ends.filter(n => Number.isInteger(n) && n > 0 && n <= this.fullText.length))];
    if (this.pageEnds.at(-1) !== this.fullText.length) this.pageEnds.push(this.fullText.length);
    this.pageEnds.sort((a, b) => a - b);
    this.pageIndex = Math.max(0, this.pageEnds.findIndex(end => end >= this.text.length));
  }

  nextPage(): void {
    if (!this.awaitingPage) return;
    this.awaitingPage = false;
    // A wider viewport may have joined unread text onto this page. Continue from
    // the same clock position rather than skipping it or getting stuck on page 1/1.
    if (this.readThrough >= this.pageEnds[this.pageIndex] && this.pageIndex < this.pageEnds.length - 1) this.pageIndex++;
    if (this.options.instantText) this.skip();
    this.present();
  }

  private pageLimit(): number {
    if (this.pageIndex >= this.pageEnds.length - 1) return this.duration;
    const end = this.pageEnds[this.pageIndex];
    for (const segment of this.segments) for (let i = 0; i < segment.glyphs.length; i++) {
      if (segment.offset + segment.glyphs[i].start >= end) return segment.revealAt[i] - 1e-6;
    }
    return this.duration;
  }

  begin(sceneId: string): void {
    this.cancel(true);
    this.sceneId = sceneId;
    this.usedTurns.clear();
    this.fullText = this.text = "";
    this.pageEnds = []; this.pageIndex = 0; this.awaitingPage = false;
    this.time = this.duration = 0;
    this.error = null;
    this.enter("attending");
    this.present();
  }

  /** Reserve before asking the provider; replacing a turn retires all its future cues. */
  prepare(turnId: string): boolean {
    if (!this.sceneId || this.phase === "releasing" || this.usedTurns.has(turnId)) return false;
    this.usedTurns.add(turnId);
    this.turnId = turnId;
    this.accepted = false;
    this.segments = [];
    this.text = this.fullText = "";
    this.pageEnds = []; this.pageIndex = 0; this.awaitingPage = false;
    this.time = this.duration = 0;
    this.error = null;
    this.silence();
    if (this.phase !== "attending") this.enter("preparing");
    this.present();
    return true;
  }

  commit(input: unknown): boolean {
    if (!isDialoguePerformance(input) || input.sceneId !== this.sceneId || input.turnId !== this.turnId ||
        this.accepted || (this.phase !== "preparing" && this.phase !== "attending")) return false;
    this.accepted = true;
    this.compile(input);
    if (this.phase !== "attending") this.startPerformance();
    this.present();
    return true;
  }

  fail(sceneId: string, turnId: string): boolean {
    if (sceneId !== this.sceneId || turnId !== this.turnId || this.accepted) return false;
    this.turnId = null;
    this.error = "The reply did not arrive. Try again, or leave the conversation.";
    this.enter("listening");
    this.silence();
    this.present();
    return true;
  }

  setOptions(options: Partial<PresentationOptions>): void {
    this.options = { ...this.options, ...options };
    if (this.options.instantText) this.skip();
    this.present();
  }

  skip(): void {
    if (this.phase !== "performing") return;
    this.time = Math.max(this.time, this.pageLimit());
    this.text = this.fullText.slice(0, this.pageEnds[this.pageIndex] ?? this.fullText.length);
    this.readThrough = this.text.length;
    if (this.pageIndex < this.pageEnds.length - 1) { this.awaitingPage = true; this.silence(); }
    else this.finishPerformance();
    this.present();
  }

  /** Close/invalid scene retires the identity immediately, even while easing back to work. */
  cancel(immediate = false): void {
    this.sceneId = this.turnId = null;
    this.awaitingPage = false;
    this.accepted = false;
    this.segments = [];
    this.error = null;
    this.silence();
    if (immediate) { this.enter("activity"); this.actor.apply(null); }
    else if (this.phase !== "activity" && this.phase !== "releasing") {
      this.releaseFrom = this.pose;
      this.enter("releasing");
    }
  }

  advance(dt: number): void {
    if (!Number.isFinite(dt) || dt < 0) return;
    if (this.awaitingPage) { this.present(); return; }
    // Consume overshoot across transitions. A slow frame samples the current pose;
    // it never queues/replays the gestures and syllables that were missed.
    let remaining = dt;
    for (let transitions = 0; transitions < 5; transitions++) {
      const limit = this.phase === "attending" ? ATTEND : this.phase === "releasing" ? RELEASE :
        this.phase === "preparing" ? PREPARE_TIMEOUT : this.phase === "performing" ? this.pageLimit() : Infinity;
      const elapsed = this.phase === "performing" ? this.time : this.phaseTime;
      const step = Math.min(remaining, Math.max(0, limit - elapsed));
      this.phaseTime += step;
      if (this.phase === "performing") this.time += step;
      remaining -= step;
      if (elapsed + step < limit) break;
      if (this.phase === "attending") {
        if (this.accepted) this.startPerformance();
        else this.enter(this.turnId ? "preparing" : "listening");
      } else if (this.phase === "performing") {
        if (this.pageIndex < this.pageEnds.length - 1) {
          this.text = this.fullText.slice(0, this.pageEnds[this.pageIndex]);
          this.readThrough = this.text.length;
          this.awaitingPage = true; this.silence(); break;
        }
        this.text = this.fullText;
        this.finishPerformance();
      } else if (this.phase === "releasing") this.enter("activity");
      else if (this.phase === "preparing") this.fail(this.sceneId!, this.turnId!);
      else break;
      if (remaining <= 0) break;
    }
    this.present();
  }

  get snapshot() {
    const current = this.phase === "performing" ? this.segments.find(s => this.time >= s.start && this.time < s.end) : undefined;
    return {
      phase: this.phase, sceneId: this.sceneId, turnId: this.turnId, text: this.text, fullText: this.fullText,
      time: this.time, duration: this.duration, segmentId: current?.id ?? null,
      deliveryId: current?.deliveryId ?? null, fallback: this.segments.some(s => s.fallback),
      error: this.error, pose: this.pose, spans: this.spans,
      pageIndex: this.pageIndex, pageCount: this.pageEnds.length || 1, awaitingPage: this.awaitingPage,
    };
  }

  private enter(phase: ConversationPhase): void { this.phase = phase; this.phaseTime = 0; }
  private silence(): void { this.lastChar = ""; this.actor.mouth(null); }
  private startPerformance(): void {
    this.time = 0;
    this.enter("performing");
    if (this.options.instantText) this.skip();
  }
  private finishPerformance(): void {
    this.silence();
    if (this.resume === "activity") this.cancel();
    else this.enter("listening");
  }
  private compile(input: DialoguePerformanceV1): void {
    let cursor = 0, offset = 0;
    this.spans = []; this.pageIndex = 0; this.awaitingPage = false; this.readThrough = 0;
    this.segments = input.segments.map(s => {
      const { pattern, fallback } = resolveDelivery(s.deliveryId, input.libraryVersion);
      // Seed changes only a small approved pause, never gesture meaning or size.
      const start = cursor;
      cursor += (pattern.lead + (input.seed % 3) * 0.035) / SPEECH_TEMPO;
      const glyphs = dialogueGlyphs(s.text, s.spans), chars = glyphs.map(g => g.text);
      const segmentOffset = offset;
      for (const span of s.spans ?? []) this.spans.push({ ...span, start: span.start + offset, end: span.end + offset });
      offset += s.text.length + 2;
      const revealAt = glyphs.map(glyph => {
        cursor += glyph.style.pause ?? 0;
        const at = cursor;
        cursor += glyphDelay(glyph, pattern.cps) / SPEECH_TEMPO;
        return at;
      });
      let lastWord = chars.length - 1;
      while (lastWord > 0 && !/\s/u.test(chars[lastWord - 1])) lastWord--;
      const nodAt = revealAt[lastWord];
      cursor += pattern.tail / 2;
      return { ...s, pattern, fallback, chars, glyphs, offset: segmentOffset, revealAt, start, end: cursor, nodAt };
    });
    this.duration = cursor;
    this.resume = input.resume;
    this.fullText = input.segments.map(s => s.text).join("\n\n");
    this.setPageEnds(this.paginate?.(this.fullText, this.spans) ?? [this.fullText.length]);
  }

  private present(): void {
    if (this.phase === "activity") { this.actor.apply(null); return; }
    let face = ARTISAN_LISTENING_FACE, pitch = 0, attention = 1;
    if (this.phase === "attending") attention = smooth((this.phaseTime - 0.03) / 0.28);
    if (this.phase === "releasing") {
      const k = 1 - smooth(this.phaseTime / RELEASE);
      this.pose = { face: {}, pitch: this.releaseFrom.pitch * k, attention: this.releaseFrom.attention * k };
      this.actor.apply(this.pose);
      return;
    }
    if (this.phase === "performing" && !this.awaitingPage) {
      const lines: string[] = [];
      let mouth: string | null = null, charKey = "", style: DialogueGlyph["style"] | undefined;
      for (const s of this.segments) {
        if (this.time < s.start) break;
        let count = 0;
        while (count < s.chars.length && s.revealAt[count] <= this.time) count++;
        lines.push(s.chars.slice(0, count).join(""));
        if (this.time >= s.end) continue;
        face = s.pattern.face;
        const nodTime = (this.time - s.nodAt) / 0.4;
        if (!this.options.reducedMotion && s.offset + s.text.length > this.readThrough && nodTime > 0 && nodTime < 1)
          pitch = s.pattern.nod * Math.sin(nodTime * Math.PI) ** 2;
        if (!this.options.reducedMotion && s.pattern.look === "work")
          attention = 1 - 0.85 * smooth((this.time - s.start) / 0.3) * (1 - smooth((this.time - (s.end - 0.6)) / 0.6));
        if (count > 0 && s.offset + s.glyphs[count - 1].end > this.readThrough && this.time - s.revealAt[count - 1] < 0.14) {
          mouth = s.chars[count - 1]; style = s.glyphs[count - 1].style; charKey = `${s.id}:${count}`;
        }
      }
      this.text = lines.join("\n\n");
      if (charKey !== this.lastChar) { this.lastChar = charKey; this.actor.mouth(mouth, style); }
    }
    this.pose = { face, pitch, attention };
    this.actor.apply(this.pose);
  }
}
